import * as React from 'react';
import { Button, Popconfirm, Form, Col, Row } from 'antd';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';
import * as moment from 'moment';
import autobind from 'autobind-decorator';
const FormItem = Form.Item;

import { ContextService } from 'src/core/services/context.service';
import { TextView } from 'src/core/ui/text.view';
import { UserEditorController } from 'src/app/general/users/parts/user-editor.controller';
import { ClientItemEditorController } from 'src/app/crm/clients/parts/client-item-editor.controller';
import { MarketEditorController } from 'src/app/general/market/parts/market-editor.controller';
import { RegionEditorController } from 'src/app/general/regions/parts/region-editor.controller';
import { BrandEditorController } from 'src/app/general/brands/parts/brand-editor.controller';
import { DateView } from 'src/core/ui/date.view';
import { BooleanView } from 'src/core/ui/boolean.view';
import { LanguageEditorController } from 'src/app/general/language/parts/language-editor.controller';
import { ObjectUtils } from '../../../../../core/utils/object';
import { AgentEditorController } from '../../../agents/parts/agent-editor.controller';
import { PaymentModeEditorView } from './payment-mode-editor.view';
import { FormatService } from '../../../../../core/services/format.service';
import { PaymentModal, TargetModel } from '../../parts/payment-modal';

interface QuoteGeneralPartViewProps extends ComponentViewProps<QuoteItem> {
	mode: ControlMode;
	changeStatus: (cancel?: boolean) => () => void;
	contextService: ContextService;
	onChangeField: (fieldName: string, requireUpdatePrice?: boolean, requirePolicyChecking?: boolean) => Promise<void>;
	updateClient: (quote: QuoteItem, client: ClientItem) => void;
	depositPercent: number;
	dueDateInDays: number;
	onMarketChange: (selected: MarketSummary) => void;
	openInitialDeposit: boolean;
	onSubmitInitialDeposit: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => Promise<void>;
	closeInitialDepositDialog: () => void;
	onPaymentModeChange: (paymentMode: number) => Promise<void>;
	onAgentChange: (agent: AgentSummary) => Promise<void>;
	currencyId: number;
	currencyCode: string;
	paxes: QuotePaxItem[];
}

interface QuoteGeneralPartViewState {
	currencyName: string;
	requiresDeposit: boolean;
}

@observer
export class QuoteGeneralPartView extends React.Component<QuoteGeneralPartViewProps, QuoteGeneralPartViewState> {
	@resolve
	private formatService: FormatService;

	constructor(props: Readonly<QuoteGeneralPartViewProps>) {
		super(props);

		this.state = {
			currencyName: '',
			requiresDeposit: true,
		};
	}

	private afterBrandLoad = (selected: MarketSummary): void => {
		if (!selected && this.props.model.brandId != null) {
			this.props.model.brandId = null;
		}
	}
	
	private afterMarketLoad = (selected: MarketSummary): void => {
		if (selected) {
			if (this.state.currencyName != selected.currencyDisplayName) {
				this.setState({
					currencyName: selected.currencyDisplayName,
					requiresDeposit: selected.requiresPayment,
				});
			}
			if (this.props.model.defaultTaxId == null) {
				this.props.model.defaultTaxId = selected.defaultTaxId;
			}
			this.props.onMarketChange(selected);
		} else if (this.props.model.marketId != null) {
			this.props.model.marketId = null;
		}
	}

	render(): React.ReactNode {
		const { model, onChangeField, openInitialDeposit, paxes } = this.props;
		let status;
		let icon;
		let style: any = {
			width: '100%',
		};
		switch (model.status) {
			case 1:
				status = 'primary';
				style.backgroundColor = '#ffffcd';
				style.borderColor = 'black';
				style.color = 'black';
				break;
			case 2:
				status = 'primary';
				style.backgroundColor = '#28a745';
				style.borderColor = '#28a745';
				break;
			case 3:
				status = 'primary';
				icon = 'check';
				break;
			case 4:
				status = 'danger';
				break;
		}
		let statusButton = (
			<Button
				block={true}
				disabled={this.props.mode == 'display' || this.props.contextService.isDirty}
				type={status}
				style={style}
				icon={icon}
			>
				{model.statusText}
			</Button>
		);

		if (model.status == 0 && this.props.mode == 'edit' && !this.props.contextService.isDirty) {
			statusButton = (
				<Popconfirm
					placement="left"
					title="This action changes a Quote to a Booking. Are you sure you want to continue?"
					onConfirm={this.props.changeStatus(false)}
					okText="Create booking"
					cancelText="Cancel">
					{statusButton}
				</Popconfirm>
			);
		}
		if (model.status === 1 || model.status === 2) {
			statusButton = (
				<Popconfirm
					placement="left"
					title="You can cancel the Booking now. Are you sure you want to continue?"
					onConfirm={this.props.changeStatus(true)}
					okText="Cancel booking"
					cancelText="Cancel">
					{statusButton}
				</Popconfirm>
			);
		}

		const targetOptions: TargetModel[] = model.agentId
			? [
				{
					id: model.agentId,
					firstName: model.agentName,
					lastName: model.agentName,
					displayName: model.agentName,
				}
			]
			: paxes
				.filter(m => m.dynamicProperties.billingTo === m.clientId)
				.map(m => ({
					id: m.clientId,
					firstName: (m.dynamicProperties['foreName'] + ' ' + (m.dynamicProperties['middleName'] || '')).trim(),
					lastName: m.dynamicProperties['surName'],
					displayName: m.clientDisplayName,
				}));

		return (
			<React.Fragment>
				{
					openInitialDeposit &&
					<PaymentModal
						editableAmount
						accountingTarget={model.agentId ? 2 : 1}
						quoteId={model.id}
						bookingDate={model.reservationDate}
						serviceDate={model.dateFrom}
						defaultCurrencyId={this.props.currencyId}
						defaultCurrencyCode={this.props.currencyCode}
						onSubmitPayment={this.props.onSubmitInitialDeposit}
						onCancelDialog={this.props.closeInitialDepositDialog}
						title="Register Deposit"
						amount={model.deposit}
						minAmount={model.deposit}
						maxAmount={model.totalSaleGross}
						target={{
							options: targetOptions
						}}
						setBusy={this.props.setBusy}>
					</PaymentModal>
				}
				<Row gutter={16}>
					<Col xs={24} lg={12}>
						<ClientItemEditorController
							required
							onGetErrorMessage={() => model.validate(false, 'clientId')}
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={model.clientId}
							onChanged={newVal => {
								this.props.updateClient(model, newVal);
								onChangeField('clientId', false, true);
							}}
						/>
						<AgentEditorController
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={model.agentId}
							onChanged={this.props.onAgentChange}
						/>
						<RegionEditorController
							label="Region"
							mode={this.props.mode}
							onChanged={(newValue) => {
								model.travelRegionId = newValue != null ? newValue.id : null;
								onChangeField('travelRegionId', false, true);
							}}
							value={model.travelRegionId}
							setBusy={this.props.setBusy}
						/>
						<DateView
							label="Reservation"
							mode="display"
							value={model.reservationDate}
							format="small-date"
							dateUsage="utc"
						/>
						<DateView
							label="From"
							mode="display"
							value={model.dateFrom}
							format="small-date"
							dateUsage="utc"
						/>
						<DateView
							label="To"
							mode="display"
							value={model.dateTo}
							format="small-date"
							dateUsage="utc"
						/>
						<LanguageEditorController
							required
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={model.languageCode}
							onGetErrorMessage={() => model.validate(false, 'languageCode')}
							onChanged={(newValue) => {
								model.languageCode = newValue
								onChangeField('languageCode');
							}}
						/>
						<UserEditorController
							required
							label="Salesman"
							onGetErrorMessage={() => model.validate(false, 'managedById')}
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							isRole={false}
							value={model.managedById}
							onChanged={(newValue) => {
								model.managedById = newValue ? newValue.id : undefined;
								onChangeField('managedById');
							}}
						/>
					</Col>
					<Col xs={24} lg={12}>
						<BrandEditorController
							required
							onGetErrorMessage={() => model.validate(false, 'brandId')}
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={model.brandId}
							idAgent={model.agentId}
							onChanged={(newValue) => {
								if (newValue != null) {
									let prefixCurrent = model.quoteNumber.substring(0, model.quoteNumber.length - 6);
									let quoteNumberNew = model.quoteNumber.replace(prefixCurrent, newValue.prefix);
									model.quoteNumber = quoteNumberNew;
									model.brandId = newValue.id;
								} else {
									model.brandId = null;
								}
								onChangeField('brandId', false, true);
							}}
							afterLoad={this.afterBrandLoad}
						/>
						<MarketEditorController
							required
							label={`Market (${this.state.currencyName})`}
							onGetErrorMessage={() => model.validate(false, 'marketId')}
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={model.marketId}
							idAgent={model.agentId}
							onChanged={(newValue) => {
								if (newValue != null) {
									model.marketId = newValue.id;
									model.defaultTaxId = newValue.defaultTaxId;
								} else {
									model.marketId = null;
									model.defaultTaxId = null;
								}

								this.setState({
									currencyName: newValue.currencyDisplayName,
									requiresDeposit: newValue.requiresPayment,
								});

								onChangeField('marketId', true, true);
							}}
							afterLoad={this.afterMarketLoad}
						/>
						<Row gutter={16}>
							<Col xs={24} lg={8}>
								<Row gutter={16}>
									<Col xs={24}>
										<TextView
											label="Total Cost"
											mode="display"
											value={model.totalCost}
											suffix={this.state.currencyName}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											label="Total Sell"
											mode="display"
											value={model.totalSale}
											suffix={this.state.currencyName}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											label="Total Sell with Taxes"
											mode="display"
											value={model.totalSaleGross}
											suffix={this.state.currencyName}
										/>
									</Col>
									{
										model.agentId &&
										<Col xs={24}>
											<TextView
												label="Commissionable (for Agent)"
												mode="display"
												value={model.dynamicProperties.sellCommission ? model.dynamicProperties.sellCommission.commissionable : 0}
												suffix={this.state.currencyName}
											/>
										</Col>
									}
								</Row>
							</Col>
							<Col xs={24} lg={8}>
								<Row gutter={16}>
									<Col xs={24}>
										<TextView
											label={`Margin for salesman (${ObjectUtils.round(100 * model.totalSalesmanProfit / (model.totalSalesmanProfit + model.totalCost))}%)`}
											mode="display"
											value={ObjectUtils.round(model.totalSalesmanProfit)}
											suffix={this.state.currencyName}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											label="Paid from Clients"
											mode="display"
											value={model.paid}
											suffix={this.state.currencyName}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											label="Remaining from Clients"
											mode="display"
											value={ObjectUtils.round(model.totalSaleGross - model.paid)}
											suffix={this.state.currencyName}
										/>
									</Col>
									{
										model.agentId &&
										<Col xs={24}>
											<TextView
												label={`Commission (${this.formatService.toString("percent-full", model.dynamicProperties.sellCommission ? model.dynamicProperties.sellCommission.percent : 0)} for Agent)`}
												mode="display"
												value={model.dynamicProperties.sellCommission ? model.dynamicProperties.sellCommission.commission : 0}
												suffix={this.state.currencyName}
											/>
										</Col>
									}
								</Row>
							</Col>
							<Col xs={24} lg={8}>
								<Row gutter={16}>
									<Col xs={24}>
										<TextView
											label={`Deposit (${model.overrideDeposit ? "Manual" : ObjectUtils.round(this.props.depositPercent * 100) + "% from Total Sell with Taxes"})`}
											mode={model.overrideDeposit ? this.props.mode : "display"}
											value={model.deposit}
											suffix={this.state.currencyName}
											onChanged={model.overrideDeposit ? e => model.deposit = e : undefined}
											addonBefore={<BooleanView
												value={model.overrideDeposit}
												mode={this.state.requiresDeposit ? this.props.mode : "display"}
												size="small"
												onChanged={newVal => {
													model.overrideDeposit = newVal;
													if (newVal === false) {
														model.deposit = ObjectUtils.round(model.totalSaleGross * this.props.depositPercent);
													}
												}}
												label={null}
											/>}
										/>
									</Col>
									<Col xs={24}>
										<DateView
											label={`Due date (${model.overrideInvoiceDueDate ? "Manual" : "Calculated"})`}
											mode={model.overrideInvoiceDueDate ? this.props.mode : "display"}
											value={model.invoiceDueDate}
											onChanged={model.invoiceDueDate ? e => model.invoiceDueDate = e : undefined}
											format="small-date"
											addonBefore={<BooleanView
												value={model.overrideInvoiceDueDate}
												mode={this.props.mode}
												size="small"
												onChanged={newVal => {
													model.overrideInvoiceDueDate = newVal;
													if (newVal === false) {
														model.invoiceDueDate = moment.utc(model.dateFrom).subtract(this.props.dueDateInDays, "day").toDate();
													}
												}}
												label={null}
											/>}
											dateUsage="utc"
										/>
									</Col>
									<Col xs={24}>
										<FormItem label='&nbsp;' colon={false}>
											{statusButton}
										</FormItem>
									</Col>
									<Col xs={24}>
										{
											model.agentId &&
											<PaymentModeEditorView
												required={true}
												label="Payment mode"
												mode={this.props.mode}
												value={model.paymentMode}
												onChanged={newval => {
													model.paymentMode = newval;
													this.props.onPaymentModeChange(newval);
												}}
											/>
										}
									</Col>
								</Row>
							</Col>
						</Row>
					</Col>
				</Row>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/parts/quote-general.view.tsx